import React, { useState, useEffect } from 'react';
import { Form, Input } from 'antd';
import { useAuthStore } from '@stores/auth.store';
import AppModal from '@/pages/inside/tenant/AppModal';
import { CreateRoleModalBody, CreateRoleModalFooter } from '@/pages/inside/tenant/style';
import { Button } from '@/components/ui/Button';
import RichTextEditorWithAttachments from '@/components/ui/RichTextEditorWithAttachments';
import AddUrlModal from '@/components/module/projects/detail_tab/AddUrlModal';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionFormItemStyle,
    submissionRichDescriptionLabelStyle,
} from '@/pages/inside/modules/projects/detail/projectTabStyles';
import { createBugReport } from '@/services/bugReport.service';

export const CreateBugReportModal = ({ open, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [pendingLinks, setPendingLinks] = useState([]);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [addLinkModalOpen, setAddLinkModalOpen] = useState(false);
    const user = useAuthStore((s) => s.user);

    useEffect(() => {
        if (!open) {
            setPendingLinks([]);
            setPendingFiles([]);
            form.resetFields();
        }
    }, [open, form]);

    const handleSubmit = () => {
        form.validateFields().then(async (values) => {
            try {
                setSubmitting(true);
                const report = await createBugReport(
                    {
                        title: values.title,
                        description: values.description,
                        links: pendingLinks,
                        files: pendingFiles,
                    },
                    user,
                );
                form.resetFields();
                setPendingLinks([]);
                setPendingFiles([]);
                onSuccess?.(report);
                onCancel();
            } catch (err) {
                console.error('Submit bug report error:', err);
            } finally {
                setSubmitting(false);
            }
        });
    };

    return (
        <AppModal
            size="md"
            title="Report a Bug"
            open={open}
            onCancel={onCancel}
            footer={null}
            styles={{ body: { paddingTop: 8 } }}
        >
            <CreateRoleModalBody $flush $noMaxHeight>
                <Form form={form} layout="vertical" requiredMark={false} styles={projectSprintTaskModalFormStyles}>
                    <Form.Item
                        name="title"
                        label={<span style={submissionRichDescriptionLabelStyle}>Title</span>}
                        rules={[{ required: true, message: 'Please enter bug title' }]}
                    >
                        <Input placeholder="e.g. Leave balance badge calculation glitch on mobile view" />
                    </Form.Item>

                    <Form.Item
                        style={submissionRichDescriptionFormItemStyle}
                        label={<span style={submissionRichDescriptionLabelStyle}>Description</span>}
                        rules={[{ required: true, message: 'Please enter bug description' }]}
                    >
                        <RichTextEditorWithAttachments
                            form={form}
                            fieldName="description"
                            open={open}
                            placeholder="Add description — bold, lists. Attach files or links."
                            
                            pendingFiles={pendingFiles}
                            onFilesSelected={async (files) => {
                                const fileList = Array.from(files);
                                const fileItems = await Promise.all(
                                    fileList.map(async (file) => {
                                        let preview = '';
                                        if (file instanceof File && file.type?.startsWith('image/')) {
                                            preview = await new Promise((resolve) => {
                                                const reader = new FileReader();
                                                reader.onload = (e) => resolve(e.target?.result || '');
                                                reader.onerror = () => resolve('');
                                                reader.readAsDataURL(file);
                                            });
                                        }
                                        return {
                                            id: Math.random().toString(36).substring(2, 9),
                                            file,
                                            name: file.name,
                                            type: file.type,
                                            preview,
                                        };
                                    })
                                );
                                setPendingFiles((prev) => [...prev, ...fileItems]);
                            }}
                            onRemovePendingFile={(id) => {
                                setPendingFiles((prev) => prev.filter((f) => f.id !== id));
                            }}
                            
                            pendingLinks={pendingLinks}
                            onRemovePendingLink={(id) => {
                                setPendingLinks((prev) => prev.filter((l) => l.id !== id));
                            }}
                            onLinkRequest={() => setAddLinkModalOpen(true)}
                        />
                    </Form.Item>
                </Form>
            </CreateRoleModalBody>
            <CreateRoleModalFooter>
                <Button $primary={false} onClick={onCancel} $padding="10px 16px" $size="14px">
                    Cancel
                </Button>
                <Button
                    $primary
                    $blackOnLight
                    onClick={handleSubmit}
                    loading={submitting}
                    $padding="10px 16px"
                    $size="14px"
                >
                    Create
                </Button>
            </CreateRoleModalFooter>

            <AddUrlModal
                open={addLinkModalOpen}
                onCancel={() => setAddLinkModalOpen(false)}
                onSubmit={(url, title) => {
                    setPendingLinks((prev) => [...prev, { id: Math.random().toString(36).substring(2, 9), url, title }]);
                    setAddLinkModalOpen(false);
                }}
            />
        </AppModal>
    );
};

export default CreateBugReportModal;
